import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    FaRunning, FaBasketballBall, FaHockeyPuck, FaVolleyballBall,
    FaSave, FaDownload, FaCheck, FaFutbol, FaRobot, FaSync, FaCalendarAlt
} from 'react-icons/fa';
import './SportKPITable.css';
import API_BASE_URL from '../../config/api';

const AVAILABLE_SEASONS = [
    '2023/2024',
    '2024/2025',
    '2025/2026',
    '2026/2027',
    '2027/2028',
    '2028/2029'
];

const getDefaultActiveSeason = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-12
    const startYear = currentMonth >= 9 ? currentYear : currentYear - 1;
    return `${startYear}/${startYear + 1}`;
};

// ── Column groups generator per season (layout only — no hardcoded data) ──────
const getColumnGroupsForSeason = (season) => {
    const parts = (season || '').split('/');
    let startYear = parseInt(parts[0], 10);
    let endYear = parseInt(parts[1], 10);
    if (isNaN(startYear) || isNaN(endYear)) {
        const now = new Date();
        startYear = now.getMonth() + 1 >= 9 ? now.getFullYear() : now.getFullYear() - 1;
        endYear = startYear + 1;
    }

    const startShort = String(startYear).slice(-2);
    const endShort = String(endYear).slice(-2);

    const q1Key = `${startYear}-12`;
    const q2Key = `${endYear}-03`;
    const q3Key = `${endYear}-06`;

    return [
        {
            group: 'Nº Atletas Inscritos',
            cols: [
                { key: `atletasInscritos_${q1Key}`, label: `Dez ${startShort}`, kpiBase: 'atletasInscritos', period: q1Key, isTrimestrual: true },
                { key: `atletasInscritos_${q2Key}`, label: `Mar ${endShort}`,   kpiBase: 'atletasInscritos', period: q2Key, isTrimestrual: true },
                { key: `atletasInscritos_${q3Key}`, label: `Jun ${endShort}`,   kpiBase: 'atletasInscritos', period: q3Key, isTrimestrual: true },
            ],
        },
        {
            group: 'Nº Atletas Federados',
            cols: [
                { key: `atletasFederados_${q1Key}`, label: `Dez ${startShort}`, kpiBase: 'atletasFederados', period: q1Key, isTrimestrual: true },
                { key: `atletasFederados_${q2Key}`, label: `Mar ${endShort}`,   kpiBase: 'atletasFederados', period: q2Key, isTrimestrual: true },
                { key: `atletasFederados_${q3Key}`, label: `Jun ${endShort}`,   kpiBase: 'atletasFederados', period: q3Key, isTrimestrual: true },
            ],
        },
        {
            group: 'Operacional',
            cols: [
                { key: 'taxaAssiduidade',  label: 'Taxa Assiduidade (%)', kpiBase: 'taxaAssiduidade',  period: season },
                { key: 'nMedioTreinos',    label: 'Nº Médio Treinos',     kpiBase: 'nMedioTreinos',    period: season },
                { key: 'nJogos',           label: 'Nº Jogos',             kpiBase: 'nJogos',           period: season },
                { key: 'nJogosTransporte', label: 'Jogos c/ Transporte',  kpiBase: 'nJogosTransporte', period: season },
            ],
        },
        {
            group: 'Abandono',
            cols: [
                { key: 'nDesistentes', label: 'Nº Desistentes',    kpiBase: 'nDesistentes', period: season },
                { key: 'taxaAbandono', label: 'Taxa Abandono (%)', kpiBase: 'taxaAbandono', period: season },
            ],
        },
        {
            group: 'Bolsas',
            cols: [
                { key: 'bolsasEscalao1', label: 'Escalão 1', kpiBase: 'bolsasEscalao1', period: season },
                { key: 'bolsasEscalao2', label: 'Escalão 2', kpiBase: 'bolsasEscalao2', period: season },
            ],
        },
        {
            group: 'Receitas',
            cols: [
                { key: 'receitasMensalidades', label: 'Mensalidades (€)', kpiBase: 'receitasMensalidades', period: season },
                { key: 'receitasPatrocinios',  label: 'Patrocínios (€)',   kpiBase: 'receitasPatrocinios',  period: season },
            ],
        },
        {
            group: 'Custos',
            cols: [
                { key: 'custoInscricao',    label: 'Inscrição (€)',   kpiBase: 'custoInscricao',    period: season },
                { key: 'custosArbitragem',  label: 'Arbitragem (€)',  kpiBase: 'custosArbitragem',  period: season },
                { key: 'custosTransportes', label: 'Transportes (€)', kpiBase: 'custosTransportes', period: season },
                { key: 'custoTreinadores',  label: 'Treinadores (€)', kpiBase: 'custoTreinadores',  period: season },
            ],
        },
        {
            group: 'Resultado',
            cols: [
                { key: 'netNet', label: 'NET NET (€)', kpiBase: 'netNet', period: season, isNetNet: true },
            ],
        },
    ];
};

// Sport icon mapping
const SPORT_ICONS = {
    'Atletismo':      <FaRunning />,
    'Basquetebol':    <FaBasketballBall />,
    'Futsal':         <FaFutbol />,
    'Hóquei Patins':  <FaHockeyPuck />,
    'Voleibol':       <FaVolleyballBall />,
};

// ── Component ─────────────────────────────────────────────────────────────────
const SportKPITable = () => {
    const [sports, setSports]                 = useState([]);       // real sports from DB
    const [selectedSport, setSelectedSport]   = useState('');
    const [selectedSeason, setSelectedSeason] = useState(getDefaultActiveSeason);
    const [teams, setTeams]                   = useState([]);       // real team names from DB
    // values: { "teamName__colKey": string }
    const [values, setValues]                 = useState({});
    // which cells are auto-calculated
    const [autoKeys, setAutoKeys]             = useState(new Set());
    // user-edited but not yet saved
    const [dirtyKeys, setDirtyKeys]           = useState(new Set());
    const [loading, setLoading]               = useState(false);
    const [loadingSports, setLoadingSports]   = useState(true);
    const [saving, setSaving]                 = useState(false);
    const [savedFlash, setSavedFlash]         = useState(false);
    const [error, setError]                   = useState(null);

    const token = localStorage.getItem('token');

    // Dynamic columns for current selected season
    const columnGroups = useMemo(() => getColumnGroupsForSeason(selectedSeason), [selectedSeason]);
    const allCols = useMemo(() => columnGroups.flatMap(g => g.cols.map(c => ({ ...c, group: g.group }))), [columnGroups]);

    const junColKey = useMemo(() => {
        const parts = (selectedSeason || '').split('/');
        const endYear = parts[1] || '2027';
        return {
            key: `atletasInscritos_${endYear}-06`,
            label: `Jun ${String(endYear).slice(-2)}`,
        };
    }, [selectedSeason]);

    // ── Load sports list from DB ──────────────────────────────────────────
    useEffect(() => {
        const fetchSports = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/sportkpi/sports`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const list = res.data ?? [];
                setSports(list);
                if (list.length > 0) setSelectedSport(list[0]);
            } catch (e) {
                console.error('Error loading sports:', e);
                setError('Não foi possível carregar as modalidades.');
            } finally {
                setLoadingSports(false);
            }
        };
        fetchSports();
    }, [token]);

    // ── Load data (teams + prefilled KPIs) when sport or season changes ───
    const loadData = useCallback(async (sport, seasonToLoad) => {
        const targetSeason = seasonToLoad || selectedSeason;
        if (!sport) return;
        setLoading(true);
        setError(null);
        setValues({});
        setAutoKeys(new Set());
        setDirtyKeys(new Set());

        try {
            const res = await axios.get(`${API_BASE_URL}/api/sportkpi/data`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { sport, season: targetSeason },
            });

            const { teams: dbTeams, rows } = res.data;
            setTeams(dbTeams ?? []);

            const targetCols = getColumnGroupsForSeason(targetSeason).flatMap(g => g.cols);

            // Flatten rows into values/autoKeys maps
            const newValues = {};
            const newAutoKeys = new Set();

            (rows ?? []).forEach(row => {
                const teamName = row.teamName;
                const kpis = row.kpis ?? {};

                targetCols.forEach(col => {
                    const stateKey = `${teamName}__${col.key}`;
                    const kpiData = kpis[col.key];

                    if (kpiData && kpiData.value !== null && kpiData.value !== undefined) {
                        newValues[stateKey] = String(kpiData.value);
                        if (kpiData.isAuto) newAutoKeys.add(stateKey);
                    } else {
                        newValues[stateKey] = '';
                    }
                });
            });

            setValues(newValues);
            setAutoKeys(newAutoKeys);
        } catch (e) {
            console.error('Error loading KPI data:', e);
            setError('Erro ao carregar dados da base de dados.');
        } finally {
            setLoading(false);
        }
    }, [token, selectedSeason]);

    useEffect(() => {
        loadData(selectedSport, selectedSeason);
    }, [selectedSport, selectedSeason, loadData]);

    // ── Input change ──────────────────────────────────────────────────────
    const handleChange = (teamName, colKey, val) => {
        const k = `${teamName}__${colKey}`;
        setValues(prev => ({ ...prev, [k]: val }));
        setDirtyKeys(prev => new Set([...prev, k]));
        setAutoKeys(prev => { const s = new Set(prev); s.delete(k); return s; });
    };

    // ── Save ──────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const items = [];
            teams.forEach(teamName => {
                allCols.forEach(col => {
                    const k = `${teamName}__${col.key}`;
                    const raw = values[k];
                    if (raw === '' || raw === null || raw === undefined) return;
                    const numVal = parseFloat(raw);
                    if (isNaN(numVal)) return;
                    items.push({
                        sport: selectedSport,
                        escalao: teamName,        // stored as "escalao" in DB
                        kpiKey: col.kpiBase,
                        period: col.period,       // uses season for annual or YYYY-MM for quarterly
                        value: numVal,
                    });
                });
            });

            await axios.post(`${API_BASE_URL}/api/sportkpi`, items, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setDirtyKeys(new Set());
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2500);
            await loadData(selectedSport, selectedSeason);
        } catch (e) {
            console.error('Error saving:', e);
            setError('Erro ao guardar. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    // ── Export CSV ────────────────────────────────────────────────────────
    const handleExport = () => {
        const headers = ['Equipa', ...allCols.map(c => `${c.group} — ${c.label}`)];
        const rows = teams.map(t => [
            t,
            ...allCols.map(col => values[`${t}__${col.key}`] ?? ''),
        ]);
        const csv = [headers, ...rows]
            .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `KPIs_${selectedSport}_${selectedSeason.replace('/', '-')}_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── Summary ────────────────────────────────────────────────────────────
    const summary = useMemo(() => {
        let totalAtletas = 0, totalNetNet = 0, filledCells = 0;
        teams.forEach(t => {
            const v = parseFloat(values[`${t}__${junColKey.key}`]);
            if (!isNaN(v)) totalAtletas += v;
            const nn = parseFloat(values[`${t}__netNet`]);
            if (!isNaN(nn)) totalNetNet += nn;
            allCols.forEach(col => {
                if (values[`${t}__${col.key}`] !== '') filledCells++;
            });
        });
        return { totalAtletas, totalNetNet, filledCells };
    }, [teams, values, allCols, junColKey]);

    // ── Render ─────────────────────────────────────────────────────────────
    if (loadingSports) {
        return (
            <div className="kpi-loading" style={{ marginTop: 60 }}>
                <div className="kpi-spinner" />
                A carregar modalidades…
            </div>
        );
    }

    if (sports.length === 0) {
        return (
            <div className="kpi-empty-state">
                <p>Não existem modalidades ativas na base de dados.</p>
            </div>
        );
    }

    return (
        <div className="kpi-table-container">

            {/* ── Top bar with Season & Sport Selectors ── */}
            <div className="kpi-top-bar">
                {/* ── Season Selector ── */}
                <div className="kpi-season-selector">
                    <span className="kpi-season-label">Época</span>
                    <div className="kpi-season-dropdown-wrapper">
                        <FaCalendarAlt className="kpi-season-icon" />
                        <select
                            id="kpi-season-select"
                            className="kpi-season-select"
                            value={selectedSeason}
                            onChange={(e) => setSelectedSeason(e.target.value)}
                        >
                            {AVAILABLE_SEASONS.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="kpi-sport-selector">
                    {sports.map(s => (
                        <button
                            key={s}
                            id={`kpi-sport-${s.toLowerCase().replace(/[\s]/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                            className={`kpi-sport-btn ${selectedSport === s ? 'active' : ''}`}
                            onClick={() => setSelectedSport(s)}
                        >
                            <span className="kpi-sport-icon">
                                {SPORT_ICONS[s] ?? <FaRunning />}
                            </span>
                            {s}
                        </button>
                    ))}
                </div>

                <div className="kpi-actions">
                    {savedFlash && <span className="kpi-save-indicator"><FaCheck /> Guardado</span>}
                    <button id="kpi-btn-refresh" className="kpi-btn kpi-btn-export" onClick={() => loadData(selectedSport, selectedSeason)} title="Recarregar dados">
                        <FaSync /> Recarregar
                    </button>
                    <button id="kpi-btn-export" className="kpi-btn kpi-btn-export" onClick={handleExport}>
                        <FaDownload /> CSV
                    </button>
                    <button
                        id="kpi-btn-save"
                        className="kpi-btn kpi-btn-save"
                        onClick={handleSave}
                        disabled={saving || dirtyKeys.size === 0}
                    >
                        <FaSave />
                        {saving ? 'A guardar…' : 'Guardar'}
                        {dirtyKeys.size > 0 && !saving && (
                            <span className="kpi-dirty-badge">{dirtyKeys.size}</span>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Summary badges ── */}
            <div className="kpi-summary-bar">
                <div className="kpi-summary-badge">
                    <span className="kpi-badge-label">Equipas</span>
                    <span className="kpi-badge-value blue">{teams.length}</span>
                </div>
                <div className="kpi-summary-badge">
                    <span className="kpi-badge-label">Atletas ({junColKey.label})</span>
                    <span className="kpi-badge-value blue">{summary.totalAtletas || '—'}</span>
                </div>
                <div className="kpi-summary-badge">
                    <span className="kpi-badge-label">NET NET Total</span>
                    <span className={`kpi-badge-value ${summary.totalNetNet >= 0 ? 'green' : 'coral'}`}>
                        {summary.totalNetNet !== 0
                            ? `€${summary.totalNetNet.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}`
                            : '—'}
                    </span>
                </div>
                <div className="kpi-summary-badge">
                    <span className="kpi-badge-label">Células preenchidas</span>
                    <span className="kpi-badge-value green">{summary.filledCells}</span>
                </div>
                {autoKeys.size > 0 && (
                    <div className="kpi-summary-badge kpi-auto-badge">
                        <span className="kpi-badge-label"><FaRobot style={{ marginRight: 3 }} />Auto-BD</span>
                        <span className="kpi-badge-value blue">{autoKeys.size}</span>
                    </div>
                )}
                {dirtyKeys.size > 0 && (
                    <div className="kpi-summary-badge">
                        <span className="kpi-badge-label">Por guardar</span>
                        <span className="kpi-badge-value coral">{dirtyKeys.size}</span>
                    </div>
                )}
            </div>

            {/* ── Legend ── */}
            <div className="kpi-legend">
                <span className="kpi-legend-item auto"><span className="kpi-legend-dot" />Auto-calculado da BD</span>
                <span className="kpi-legend-item dirty"><span className="kpi-legend-dot" />Alterado (por guardar)</span>
                <span className="kpi-legend-item manual"><span className="kpi-legend-dot" />Guardado manualmente</span>
            </div>

            {error && <div className="kpi-error-msg">{error}</div>}

            {/* ── Table ── */}
            <div className="kpi-table-card">
                <div className="kpi-table-card-header">
                    <h3>
                        {SPORT_ICONS[selectedSport] ?? <FaRunning />}
                        &nbsp;{selectedSport} — KPIs {selectedSeason}
                    </h3>
                    <span className="kpi-edit-hint">Todos os campos são editáveis</span>
                </div>

                {loading ? (
                    <div className="kpi-loading">
                        <div className="kpi-spinner" />
                        A carregar dados da base de dados…
                    </div>
                ) : teams.length === 0 ? (
                    <div className="kpi-loading" style={{ color: '#a0aec0' }}>
                        Sem equipas activas para esta modalidade na época {selectedSeason}.
                    </div>
                ) : (
                    <div className="kpi-table-scroll">
                        <table className="kpi-table">
                            <thead>
                                <tr className="kpi-th-group">
                                    <th rowSpan={2}>Equipa</th>
                                    {columnGroups.map(g => (
                                        <th key={g.group} colSpan={g.cols.length}>{g.group}</th>
                                    ))}
                                </tr>
                                <tr className="kpi-th-sub">
                                    {columnGroups.flatMap(g =>
                                        g.cols.map(col => (
                                            <th
                                                key={col.key}
                                                className={col.isTrimestrual ? 'period-col' : ''}
                                            >
                                                {col.label}
                                            </th>
                                        ))
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {teams.map(teamName => (
                                    <tr key={teamName}>
                                        <td className="escalao-cell" title={teamName}>{teamName}</td>
                                        {columnGroups.flatMap(g =>
                                            g.cols.map(col => {
                                                const k = `${teamName}__${col.key}`;
                                                const val = values[k] ?? '';
                                                const isDirty  = dirtyKeys.has(k);
                                                const isAuto   = autoKeys.has(k) && !isDirty;
                                                const isManual = !isDirty && !isAuto && val !== '';
                                                const isNetNet = col.isNetNet;
                                                const netVal   = isNetNet ? parseFloat(val) : 0;

                                                return (
                                                    <td
                                                        key={col.key}
                                                        className={[
                                                            col.isTrimestrual ? 'period-col' : '',
                                                            isNetNet ? 'netnet-col' : '',
                                                        ].filter(Boolean).join(' ')}
                                                    >
                                                        <input
                                                            id={`kpi-${teamName.replace(/[\s.]/g, '-')}-${col.key}`}
                                                            type="number"
                                                            step="any"
                                                            className={[
                                                                'kpi-input',
                                                                isDirty  ? 'dirty'  : '',
                                                                isAuto   ? 'auto'   : '',
                                                                isManual ? 'manual' : '',
                                                                isNetNet ? 'netnet-input' : '',
                                                                isNetNet && !isNaN(netVal) && netVal < 0 ? 'negative' : '',
                                                            ].filter(Boolean).join(' ')}
                                                            value={val}
                                                            onChange={e => handleChange(teamName, col.key, e.target.value)}
                                                            placeholder="—"
                                                            title={
                                                                isAuto   ? 'Auto-calculado da BD (editável)' :
                                                                isManual ? 'Valor guardado manualmente' :
                                                                isDirty  ? 'Alterado — por guardar' :
                                                                'Clique para editar'
                                                            }
                                                        />
                                                    </td>
                                                );
                                            })
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SportKPITable;
